import React, { useState, useEffect, useContext } from 'react';
import { UserDataContext } from '../../App';
import REST from '../../libs/API';
import { Box, Typography, Divider, Button, Pagination, Tooltip, useMediaQuery } from '@mui/material';
import { ContentBox } from "../../libs/theme";
import OrdersDialogOrder from './OrdersDialogOrder';
import { ArrowRight, OrderWarning, OrderError, OrderComplete, OrderProccess } from '../../assets/images/icons';
import PSIcon from '../../components/PSIcon';
import { EmptyBox } from '../../libs/theme';
import { formatDate, getText } from '../../libs/utils';
import { useNavigate } from 'react-router-dom';
import {styled, useTheme} from '@mui/material/styles';

const OrderRow = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '20px 0',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column'
  },
  [theme.breakpoints.only('sm')]: {
    "&:first-of-type": {
      paddingTop: '5px'
    },
    "&:last-child": {
      paddingBottom: '5px'
    },
  },
  [theme.breakpoints.only('xs')]: {
    maxWidth: '350px',
    margin: '0 auto',
    "&:first-of-type": {
      paddingTop: '10px'
    },
    "&:last-child": {
      paddingBottom: '10px'
    },
  }
}))

const OrderAmountsRow = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.only('xs')]: {
    display: 'block',
  },
  ".orderSystem": {
    display: 'flex',
    alignItems: 'center',
  },
  ".leftAmount": {
    flex: 1
  },
  ".rightAmount": {
    flex: 1,
    [theme.breakpoints.only('sm')]: {
      textAlign: 'right'
    },
    ".orderSystem": {
      [theme.breakpoints.only('sm')]: {
        justifyContent: 'right'
      },
    }
  },
}))

const Arrow = styled(Box)(({theme}) => ({
  width: 85,
  textAlign: 'center',
  paddingTop: '12px',
  position: 'relative',
  [theme.breakpoints.only('xs')]: {
    transform: 'rotate(90deg)',
    width: '100%',
    padding: '9px 0 13px',
    svg: {
      width: '19px',
      height: '19px'
    }
  }
}))

const OrderRowInfo = styled(Box)(({theme}) => ({
  minWidth: 307,
  [theme.breakpoints.down('md')]:{
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 0 0',
    alignItems: 'center'
  },
  [theme.breakpoints.only('xs')]:{
    flexDirection: 'column',
    minWidth: 0
  },
  ".orderDate": {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '15px',
    [theme.breakpoints.only('xs')]:{
      justifyContent: 'start',
      gap: '25px',
      padding: '9px 0 23px',
      width: '100%'
    }
  }
}))

const ButtonRow = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]:{
    padding: '10px 0 0'
  },
  [theme.breakpoints.down('md')]:{
    alignItems: 'center'
  },
  [theme.breakpoints.only('xs')]:{
    width: '100%'
  }
}))

const MyButton = styled(Button)(({theme}) => ({
  paddingLeft: '50px',
  paddingRight: '50px',
  [theme.breakpoints.only('md')]: {
    padding: '12px 50px'
  },
  [theme.breakpoints.only('sm')]: {
    padding: '12px 20px'
  },
  [theme.breakpoints.only('xs')]: {
    padding: '12px 35px'
  }
}))

const TooltipPointer = styled(Box)(() => ({
  cursor: 'pointer'
}))

const Orders = () => {
  const userData = useContext(UserDataContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [pagination, setPagination] = useState({ perPage: 10, currentPage: 1, pageCount: 1 });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  let SMnXS = useMediaQuery(theme.breakpoints.down('md'));
  let IsXS = useMediaQuery(theme.breakpoints.only('xs'));

  useEffect(() => {
    getOrders();
  }, []);

  const getOrders = (page = pagination.currentPage) => {
    setLoading(true);
    REST.getOrdersList(userData.token, {
      perPage: pagination.perPage,
      page,
    }).then(data => {
      if (data.success) {
        setOrders(data.data);
        setPagination(data.pagination)
      }
      setLoading(false);
    });
  }

  const handleChangePagination = (event, value) => {
    setPagination({ ...pagination, currentPage: value });
    getOrders(value);
  }

  const getTooltip = (order) => {
    const textAlias = {
      1: getText('orderProcessStteBlockSubhed6'),
      2: getText("orderErrorNeedFixHint"),
      4: getText("orderErrorHint4"),
      5: getText("orderError"),
      6: getText('orderProcessStteBlockSubhed7'),
      7: getText("orderProcessStteBlockSubhed71"),
      8: getText("orderErrorNeedFixHint"),
      9: getText("orderProcessStteBlockSubhed70"),
      10: getText("orderErrorNeedFixHint"),
    }

    const tooltipProps = {
      placement: IsXS ? 'bottom-end' : 'left-start',
      enterTouchDelay: 50,
      leaveTouchDelay: 3000,
      PopperProps: {
        modifiers: [
          // {
          //   name: 'flip',
          //   enabled: IsXS ? true : false
          // },
          {
            name: 'offset',
            options: IsXS ? { offset: [0, -15]} : {},
          },
        ],
      }
    };

    return (<>
      {[3, 11].indexOf(order.paymentStatus.id) != -1 && <span className="doneOrder">
        <Tooltip
          title={<Box>
            <Typography variant='h5'>{getText('orderHeaderState3')}</Typography>
            <Typography variant='caption' sx={{weight: 400}}>{getText('tooltipOrderDoneText')}</Typography>
            <Typography sx={{ fontWeight: '600', color: 'primary.green', margin: '15px 0 3px' }}>{order.paymentStatus.label}</Typography>
            <Typography variant='greyCaption'>{getText('orderSBStage5')}</Typography>
          </Box>}
          {...tooltipProps}
        >
          <TooltipPointer><OrderComplete /></TooltipPointer>
        </Tooltip>
      </span>}
      {[2].indexOf(order.paymentStatus.id) != -1 && <span className="description">
        <Tooltip
          title={<Box>
            <Typography variant='h5'>{getText('tooltipTitleWanr')}</Typography>
            <Typography variant='caption' sx={{weight: 400}}>{textAlias[order.paymentStatus.id]}</Typography>
            <Typography sx={{ fontWeight: '600', color: 'primary.warning', margin: '15px 0 3px' }}>
              {order.description ? order.description : order.paymentStatus.label}
            </Typography>
            <Typography variant='greyCaption'>{getText('orderErrorReasonCaption')}</Typography>
          </Box>}
          {...tooltipProps}
        >
          <TooltipPointer><OrderWarning /></TooltipPointer>
        </Tooltip>
      </span>}
      {[4, 5, 8, 10].indexOf(order.paymentStatus.id) != -1 && <span className="errorOrder">
        <Tooltip
          title={<Box>
            <Typography variant='h5'>{getText('tooltipTitleError')}</Typography>
            <Typography variant='caption' sx={{weight: 400}}>{textAlias[order.paymentStatus.id]}</Typography>
            <Typography sx={{ fontWeight: '600', color: 'primary.darkRed', margin: '15px 0 3px' }}>
              {order.description ? order.description : order.paymentStatus.label}
            </Typography>
            <Typography variant='greyCaption'>{getText('orderErrorReasonCaption')}</Typography>
          </Box>}
          {...tooltipProps}
        >
          <TooltipPointer><OrderError /></TooltipPointer>
        </Tooltip>
      </span>}
      {[1, 6, 7, 9].indexOf(order.paymentStatus.id) != -1 && <span className="orderStatus">
        <Tooltip
          title={<Box>
            <Typography variant='h5'>{getText('tooltipTitleProcess')}</Typography>
            <Typography variant='caption' sx={{weight: 400}}>{textAlias[order.paymentStatus.id]}</Typography>
            <Typography sx={{ fontWeight: '600', color: 'primary.green', margin: '15px 0 3px' }}>{order.paymentStatus.label}</Typography>
            <Typography variant='greyCaption'>{getText('orcardStateCol')}</Typography>
          </Box>}
          {...tooltipProps}
        >
          <TooltipPointer><OrderProccess /></TooltipPointer>
        </Tooltip>
      </span>}
    </>)
  }

  return (
    <>
      <ContentBox sx={{ padding: {xs: '12px 23px', sm: '15px 32px'}, minHeight: 486 }}>
        {loading
          ? <span className="dot-spin centered"></span>
          : !orders.length
            ? <EmptyBox>
                <Typography sx={{ margin: '0 0 25px' }}>{getText('ordersEmptyText')}</Typography>
                <Button sx={{ padding: '14px 53px' }} variant="contained" onClick={() => navigate('/')}>{getText('orderCreate')}</Button>
              </EmptyBox>
            : orders.map((order, key) => (
              <React.Fragment key={key} >
                <OrderRow>
                  <OrderAmountsRow>
                    <Box className='leftAmount'>
                      <Typography variant="orderId">{order.amount.value} {order.fromCurrency}</Typography>
                      <Typography className="orderSystem" variant="div">
                        <PSIcon
                          name={order.fromPaymentSystemClassName}
                          network={order.fromCurrencyNetwork}
                          height={28}
                          width={28}
                          margin="0 8px 0 0"
                        />
                        {order.fromPaymentSystem} 
                      </Typography>
                    </Box>

                    <Arrow><ArrowRight width="23" height="23" /></Arrow>

                    <Box className='rightAmount'>
                      <Typography variant="orderId">{order.toAmount.value} {order.toCurrency}</Typography>
                      <Typography className="orderSystem" variant="div">
                        <PSIcon
                          name={order.toPaymentSystemClassName}
                          network={order.toCurrencyNetwork}
                          height={28}
                          width={28}
                          margin="0 5px 0 0"
                        />
                        {order.toPaymentSystem}
                      </Typography>
                    </Box>
                  </OrderAmountsRow>
                  <OrderRowInfo>
                    <Box className='orderDate'>
                      <Box>
                        <Typography variant="boldRegCaption">{order.id}</Typography>
                        <Typography variant="caption" sx={{ color: "primary.greyText" }} component="div">{getText('orcardIdCol')}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="boldRegCaption">{formatDate(new Date(order.createdAt))}</Typography>
                        <Typography variant="caption" sx={{ color: "primary.greyText" }} component="div">{getText('orcardDateCol')}</Typography>
                      </Box>

                      {!SMnXS && getTooltip(order)}
                    </Box>
                    <ButtonRow>
                      <MyButton
                        variant='contained'
                        onClick={() => window.open(`/status/${order.code}`, '_blank')}
                      >
                        {getText('orcardGoOrder')}
                      </MyButton>
                      
                      <OrdersDialogOrder order={order}/>
                      
                      {SMnXS && getTooltip(order)}
                    </ButtonRow>
                  </OrderRowInfo>
                </OrderRow>
                {orders.length !== (key + 1) && <Divider sx={{ margin: {lg: '15px 0 20px', xs: '10px 0 15px'} }} />}
              </React.Fragment>
            ))
        }
      </ContentBox>

      {pagination.pageCount > 1 && <Pagination count={pagination.pageCount} hideNextButton={true} hidePrevButton={true} onChange={handleChangePagination} />}
    </>
  )
}

export default Orders